import { getFormatDetailRows } from '@src/utility/distributionChannelComparison';
import { Fragment } from 'react';
import styled from 'styled-components';

import ChannelIcon from './ChannelIcon';

import type {
  DistributionChannel,
  DistributionChannelFormat,
} from '@src/types.ts/distribution-channel';
import type { CSSProperties } from 'react';

interface Args {
  channel: DistributionChannel;
  format: DistributionChannelFormat | undefined;
  unsupportedTitle?: string;
}

/**
 * Inline style applied to the react-tooltip `<Tooltip>` element to strip its
 * default chrome. All visual styling is handled by the `TooltipBody` wrapper
 * rendered inside the tooltip content.
 */
export const CHANNEL_TOOLTIP_STYLE: CSSProperties = {
  background: 'none',
  border: 'none',
  borderRadius: 0,
  padding: 0,
  boxShadow: 'none',
  zIndex: 100001,
};

/**
 * Canonical hover-tooltip content for a (channel, format) pair. Used by the
 * Compare summary table and the list-view summary so both surfaces render the
 * same overlay when a user hovers a support mark or an envelope name.
 */
export const renderChannelFormatTooltip = ({
  channel,
  format,
  unsupportedTitle,
}: Args) => (
  <TooltipBody>
    <TooltipChannelHeader>
      <ChipBox>
        <ChannelIcon
          iconUrl={channel.iconUrl}
          alt={`${channel.title} icon`}
          size={20}
          variant="plain"
        />
      </ChipBox>
      <TooltipChannelName>{channel.title}</TooltipChannelName>
    </TooltipChannelHeader>
    <TooltipTitle>
      {format
        ? format.title
        : (unsupportedTitle ?? `Not supported on ${channel.title}`)}
    </TooltipTitle>
    {format && (
      <TooltipGrid>
        {getFormatDetailRows(format).map(row => (
          <Fragment key={row.aspect}>
            <TooltipLabel>{row.label}</TooltipLabel>
            <TooltipValue>{row.value}</TooltipValue>
          </Fragment>
        ))}
      </TooltipGrid>
    )}
  </TooltipBody>
);

const TooltipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 12px;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-regular);
  text-align: left;
  color: var(--popover-foreground, var(--foreground));
  background: var(--popover, var(--surface-elevated));
  border: 1px solid var(--border-subtle, var(--border));
  border-radius: 6px;
  box-shadow: var(--elevation-sm, 0 4px 12px rgba(0, 0, 0, 0.18));
  max-width: 360px;
  pointer-events: none;
`;

// Mirrors the gray icon chip used in the comparison-table column headers, so
// the tooltip header reads as the same visual unit.
const ChipBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 1px;
  border-radius: 6px;
  box-sizing: border-box;
  background: var(--text-muted);
  border: 1px solid var(--text-muted);

  img {
    filter: brightness(0);
  }
`;

const TooltipTitle = styled.span`
  font-weight: var(--font-weight-bold);
  color: var(--foreground);
  white-space: nowrap;
`;

const TooltipGrid = styled.span`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 16px;
  row-gap: 4px;
`;

const TooltipLabel = styled.span`
  color: var(--text-secondary);
  white-space: nowrap;
`;

const TooltipValue = styled.span`
  color: var(--foreground);
  white-space: nowrap;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const TooltipChannelHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-subtle, var(--border));
`;

const TooltipChannelName = styled.span`
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  white-space: nowrap;
`;
